import React, { useEffect, useState } from "react";

import ApiClient from "../services/fuelandmoreapi";

import type { TClient, TUser } from "@/types";

import BinIcon from "./BinIcon";

import AddClient from "./AddClient";

import { FeedbackText } from "./FeedbackText";

import "./ClientList.css";

interface ClientListProps {
  apiUrl: string;
  selectedClientDomain: string | null;
  setSelectedClientDomain: (domain: string) => void;
  enableAddClient?: boolean;
}

const ClientList: React.FC<ClientListProps> = ({
  apiUrl,
  selectedClientDomain,
  setSelectedClientDomain,
  enableAddClient = false,
}) => {
  const [clients, setClients] = useState<TClient[]>([]);
  const [accountManagers, setAccountManagers] = useState<TUser[]>([]);
  const [showAddClient, setShowAddClient] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [pendingChange, setPendingChange] = useState<{
    domain: string;
    originalEmail: string;
    newEmail: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const apiClient = new ApiClient(apiUrl);

  const fetchClients = async () => {
    try {
      setError(null);
      const response = await apiClient.getClients();
      setClients(response.data);
    } catch (err) {
      console.error("Error fetching clients:", err);
    }
  };

  const fetchAccountManagers = async () => {
    try {
      const managers = await apiClient.getAccountManagers();
      setAccountManagers(managers || []);
    } catch {
      setAccountManagers([]);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchAccountManagers();
    // eslint-disable-next-line
  }, []);

  const handleClientAdded = () => {
    setShowAddClient(false);
    fetchClients();
  };

  const handleDeleteClient = async (clientDomain: string) => {
    try {
      setConfirmDelete(null);

      const response = await apiClient.deleteClient(clientDomain);

      if (response.status < 200 || response.status >= 300) {
        setError(response.data?.message || "Failed to delete client.");
        return;
      }

      fetchClients();
    } catch (err) {
      console.error("Error deleting client:", err);
    }
  };

  const handleAccountManagerChange = (
    domain: string,
    originalEmail: string,
    newEmail: string,
    userRole?: string | null
  ) => {
    if (userRole !== "admin") return; // only admins can change
    if (originalEmail === newEmail) return; // no change
    setPendingChange({ domain, originalEmail, newEmail });
  };

  const confirmAccountManagerChange = async () => {
    if (!pendingChange) return;
    setSaving(true);
    try {
      const client = clients.find((c) => c.domain === pendingChange.domain);
      if (!client) return;
      const updated = {
        ...client,
        accountManagerEmail: pendingChange.newEmail,
      };
      await apiClient.upsertClient(updated);
      await fetchClients();
    } catch (e) {
      setError("Failed to update client.");
    } finally {
      setSaving(false);
      setPendingChange(null);
    }
  };

  const cancelAccountManagerChange = () => {
    setPendingChange(null);
    // UI will revert because value derives from clients state
  };

  // Feature flag and role check for map link
  let showMapLinkAM = false;
  let showMapLinkAdmin = false;
  let userRole = null;
  try {
    const featureFlags = JSON.parse(localStorage.feature_flags);
    showMapLinkAM = !!featureFlags.ACCOUNTMANAGER_SHOW_MAP;
    showMapLinkAdmin = !!featureFlags.ADMIN_SHOW_MAP;
    const user = JSON.parse(localStorage.user || "{}");
    userRole = user.role || null;
  } catch (e) {
    // ignore
  }

  const handleShowMap = (e: React.MouseEvent, client: TClient) => {
    e.stopPropagation();
    window.open(
      `/map?client=${encodeURIComponent(client.domain)}`,
      `map_${client.domain.replace(/[^a-zA-Z0-9]/g, "_")}`
    );
  };

  return (
    <div className="client-list-container">
      <h3>Clients</h3>

      <div className="client-list-table-wrapper">
        <table className="client-list-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Domain</th>
              <th>Account Manager Email</th>
              {((showMapLinkAM && userRole === "accountManager") ||
                (showMapLinkAdmin && userRole === "admin")) && <th></th>}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr
                key={client.domain}
                className={
                  selectedClientDomain === client.domain
                    ? "client-row-selected"
                    : ""
                }
                onClick={() => setSelectedClientDomain(client.domain)}
                style={{ cursor: "pointer" }}
              >
                <td>{client.name}</td>
                <td>{client.domain}</td>
                <td>
                  {userRole === "admin" ? (
                    <select
                      value={client.accountManagerEmail || ""}
                      onChange={(e) =>
                        handleAccountManagerChange(
                          client.domain,
                          client.accountManagerEmail || "",
                          e.target.value,
                          userRole
                        )
                      }
                    >
                      <option value="">Select...</option>
                      {accountManagers.map((am) => (
                        <option key={am.email} value={am.email}>
                          {am.email}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span>{client.accountManagerEmail || ""}</span>
                  )}
                </td>
                {((showMapLinkAM && userRole === "accountManager") ||
                  (showMapLinkAdmin && userRole === "admin")) && (
                  <td>
                    <a
                      href="#"
                      className="show-map-link"
                      title="Show Map"
                      onClick={(e) => handleShowMap(e, client)}
                      style={{ marginRight: 8 }}
                    >
                      Show Map
                    </a>
                  </td>
                )}
                <td>
                  <button
                    className="bin-btn"
                    title="Delete Client"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDelete(client.domain);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    <BinIcon color="black" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {enableAddClient ? (
        <div className="add-client-button-container">
          {showAddClient ? (
            <AddClient
              apiClient={apiClient}
              onClientAdded={handleClientAdded}
              onClose={() => setShowAddClient(false)}
            />
          ) : (
            <button
              className="primary-button"
              onClick={() => setShowAddClient(true)}
            >
              + Add Client
            </button>
          )}
        </div>
      ) : null}
      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-message">
              Are you sure you want to delete client <b>{confirmDelete}</b>?
            </div>
            <div className="modal-actions">
              <button
                className="modal-btn confirm"
                onClick={() => handleDeleteClient(confirmDelete)}
              >
                Yes
              </button>
              <button
                className="modal-btn cancel"
                onClick={() => setConfirmDelete(null)}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm account manager change modal */}
      {pendingChange && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-message">
              Change account manager for <b>{pendingChange.domain}</b>
              <br />
              from <i>{pendingChange.originalEmail || "(none)"}</i> to
              <br />
              <i>{pendingChange.newEmail || "(none)"}</i>?
            </div>
            <div className="modal-actions">
              <button
                className="modal-btn confirm"
                disabled={saving}
                onClick={confirmAccountManagerChange}
              >
                {saving ? "Saving..." : "Yes"}
              </button>
              <button
                className="modal-btn cancel"
                disabled={saving}
                onClick={cancelAccountManagerChange}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <FeedbackText message={error} />}
    </div>
  );
};

export default ClientList;
